/**
 * FullPipelineCostCard Component
 *
 * Shows the estimated TOTAL cost of an entire video generation pipeline,
 * with an expandable per-component breakdown.
 *
 * Visual pattern: right-aligned stacked layout, consistent with TokenEstimateCard.
 * Uses the "full_video_estimate" operation endpoint.
 */

import { memo, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, Loader2, Cpu, Cloud, KeyRound } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import { handleError } from '../../utils/errorHandler';
import { fetchCostPreview } from '../../services/costPreview';
import { isServerlessMode, isElectronMode } from '../../utils/apiMode';
import { COST_GATE_UNAVAILABLE, costGateStatus, type CostGate } from '../../utils/costGate';
import { CloudLocalCostDisplay } from './CloudLocalCostDisplay';

// Per-component execution location — mirrors the backend LOCATION_* constants in
// constants/pricing/pipeline.py (single source of truth). Drives the row colour:
// local (green/user GPU), cloud (gray/billed), user_key (gray/BYOK free), and
// skipped (hidden — the component does not run for this configuration).
const LOCATION_LOCAL = 'local';
const LOCATION_USER_KEY = 'user_key';
const LOCATION_SKIPPED = 'skipped';

// ============================================================================
// Types
// ============================================================================

interface PipelineBreakdownComponent {
  component: string;
  tokens: number;
  cost_usd: number;
  detail: string;
  /**
   * "local" | "cloud" | "user_key" | "skipped" — see LOCATION_* above. Optional:
   * a not-yet-deployed Cloud Run backend omits it (older estimate response).
   */
  location?: string;
}

interface PipelineEstimateData {
  total_estimated_tokens: number;
  /**
   * Full cloud price (no local/BYOK discount) — paired with total_estimated_tokens.
   * Optional: absent until the backend that returns it is deployed.
   */
  total_cloud_tokens?: number;
  total_cost_usd: number;
  breakdown: PipelineBreakdownComponent[];
  total_local_savings_tokens: number;
  processing_seconds: number;
}

interface FullPipelineCostCardProps {
  /** Full pipeline configuration parameters */
  parameters: Record<string, unknown>;
  /**
   * Optional per-slice availability map: component_id -> true/false.
   * When provided, hybrid components that are locally available will be priced
   * at 0 tokens (local discount) in the server-side estimate.
   */
  availability?: Record<string, boolean> | null;
  /**
   * Optional BYOK key map: API key env-var name -> true/false.
   * When provided, cloud slices whose provider maps to a True entry will be
   * priced at 0 tokens (user paid their provider directly via keyring key).
   * Only meaningful in Electron; web callers may omit this or pass null/empty.
   */
  userKeys?: Record<string, boolean> | null;
  /**
   * Set when an input the price depends on is still missing (e.g. the narration
   * text of a user_provided narration, whose length sets the duration). The card
   * shows this hint instead of asking the server, which would only answer 400,
   * and reports the cost as unavailable so generation stays blocked.
   */
  pendingHint?: string | null;
  /** Whether the pipeline may run at this cost (src/utils/costGate.ts). */
  onEstimateChange?: (gate: CostGate) => void;
  /** Callback when loading state changes, useful for disabling submit buttons */
  onLoadingChange?: (isLoading: boolean) => void;
}

// ============================================================================
// Helpers
// ============================================================================

/** Map backend component names to i18n keys */
function getComponentKey(component: string): string {
  const keyMap: Record<string, string> = {
    visual_media: 'tokens.component_visual_media',
    image_generation: 'tokens.component_image_generation',
    tts: 'tokens.component_tts',
    llm_script: 'tokens.component_llm_script',
    seo: 'tokens.component_seo',
    phonetic_correction: 'tokens.component_phonetic_correction',
    subtitles: 'tokens.component_subtitles',
    detail_director: 'tokens.component_detail_director',
    resource_curator: 'tokens.component_resource_curator',
    effects_specialist: 'tokens.component_effects_specialist',
    escriba: 'tokens.component_escriba',
    chapter_divider: 'tokens.component_chapter_divider',
    transcription_adapter: 'tokens.component_transcription_adapter',
    gpu_worker: 'tokens.component_gpu_worker',
  };
  return keyMap[component] ?? component;
}

// ============================================================================
// Component
// ============================================================================

export const FullPipelineCostCard = memo(function FullPipelineCostCard({
  parameters,
  availability,
  userKeys,
  pendingHint,
  onEstimateChange,
  onLoadingChange,
}: FullPipelineCostCardProps) {
  const { t } = useTranslation();
  const { balance } = useTokenBalance();
  const [estimate, setEstimate] = useState<PipelineEstimateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Track mounted state to avoid setting state after unmount
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // AbortController for the in-flight estimate request.
  // A new controller is created on each fetch; aborting the previous one
  // cancels the stale network request before issuing the new one.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Monotonically increasing counter used as a sequence guard.
  // Even if the abort does not fire in time (e.g. slow networks),
  // a response whose counter predates the current one is silently dropped.
  const requestCounterRef = useRef(0);

  // Use refs for callback props and balance to prevent them from triggering re-fetches.
  // Without refs, any parent re-render creates new callback instances,
  // which changes fetchEstimate, which restarts the debounce timer.
  const onEstimateChangeRef = useRef(onEstimateChange);
  onEstimateChangeRef.current = onEstimateChange;
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  // Balance ref avoids including balance in fetchEstimate deps (prevents spurious API calls on balance poll)
  const balanceRef = useRef(balance);
  balanceRef.current = balance;

  // Notify parent when loading state changes (via ref to avoid dep cycle)
  useEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);

  // Serialize parameters once to ensure stable dependency
  const paramsString = useMemo(() => JSON.stringify(parameters), [parameters]);
  // Serialize availability separately so changes to it also re-trigger the estimate
  const availabilityString = useMemo(() => JSON.stringify(availability ?? null), [availability]);
  // Serialize userKeys so BYOK changes also re-trigger the estimate
  const userKeysString = useMemo(() => JSON.stringify(userKeys ?? null), [userKeys]);

  const fetchEstimate = useCallback(async () => {
    // Token billing lives on Cloud Run. Fetch in web serverless mode and in
    // Electron desktop (which routes via VITE_CLOUD_API_URL); skip in pure
    // local dev to avoid 404s against the local Flask server.
    if (!isServerlessMode() && !isElectronMode()) return;

    // Abort the previous in-flight request to prevent stale responses from
    // overwriting a more recent estimate result.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Capture a sequence number for this request. Any response whose
    // sequence predates the current counter is discarded (defensive guard
    // in case the abort signal does not fire in time on slow networks).
    requestCounterRef.current += 1;
    const thisRequestId = requestCounterRef.current;

    setLoading(true);
    setError(null);

    try {
      const parsedAvailability: Record<string, boolean> | null = JSON.parse(availabilityString);
      const parsedUserKeys: Record<string, boolean> | null = JSON.parse(userKeysString);
      const requestParams: Record<string, unknown> = JSON.parse(paramsString);
      // Embed availability into the parameters dict so the estimator receives it
      if (parsedAvailability !== null) {
        requestParams['availability'] = parsedAvailability;
      }
      // Embed BYOK user_keys so the estimator can zero matching slices
      if (parsedUserKeys !== null && Object.keys(parsedUserKeys).length > 0) {
        requestParams['user_keys'] = parsedUserKeys;
      }
      // Priced as the operation the Generate button is about to call, so the
      // number shown here is the number POST /api/v1/video-generation/generate
      // reserves.
      const data = await fetchCostPreview(
        '/api/v1/video-generation/generate',
        requestParams,
        'POST',
        controller.signal
      );

      // Drop the response if it belongs to a superseded request or the
      // component has already unmounted.
      if (!isMounted.current || thisRequestId !== requestCounterRef.current) return;

      // The preview normalises every operation's headline onto
      // estimated_tokens and keeps the estimator's own breakdown in `estimate`,
      // which for this operation is the 13-component pipeline shape below.
      const estimateData = data.estimate as unknown as PipelineEstimateData;
      setEstimate(estimateData);

      // Notify parent of estimate change (via ref to avoid dep cycle)
      onEstimateChangeRef.current?.({
        tokens: estimateData.total_estimated_tokens,
        status: costGateStatus(estimateData.total_estimated_tokens, balanceRef.current),
      });
    } catch (err) {
      // Do not update error state when the failure is due to an abort —
      // the in-progress fetch was intentionally cancelled by the next
      // keystroke, so the previous valid estimate should remain visible.
      if (err instanceof Error && err.name === 'AbortError') return;

      if (isMounted.current && thisRequestId === requestCounterRef.current) {
        setError(handleError(err, 'FullPipelineEstimate', t('tokens.estimateFailed')));
        // Block generation -- nobody can say what it would charge -- but as
        // "cost unavailable", never as "insufficient tokens".
        onEstimateChangeRef.current?.(COST_GATE_UNAVAILABLE);
      }
    } finally {
      // Only clear the loading state for the request that is currently
      // authoritative (i.e. hasn't been superseded by a newer one).
      if (isMounted.current && thisRequestId === requestCounterRef.current) {
        setLoading(false);
      }
    }
  }, [paramsString, availabilityString, userKeysString, t]);

  // Debounced fetch when parameters change
  useEffect(() => {
    if (pendingHint) {
      // Nothing to price yet: cancel and invalidate any in-flight request so a
      // late answer cannot repaint a price for inputs that no longer apply.
      abortControllerRef.current?.abort();
      requestCounterRef.current += 1;
      setEstimate(null);
      setError(null);
      setLoading(false);
      onEstimateChangeRef.current?.(COST_GATE_UNAVAILABLE);
      return;
    }
    const timer = setTimeout(() => {
      fetchEstimate();
    }, 400); // 400ms debounce (slightly longer than TokenEstimateCard since more params)

    return () => clearTimeout(timer);
  }, [fetchEstimate, pendingHint]);

  // Update parent when balance changes (via ref to avoid dep cycle)
  useEffect(() => {
    if (estimate) {
      onEstimateChangeRef.current?.({
        tokens: estimate.total_estimated_tokens,
        status: costGateStatus(estimate.total_estimated_tokens, balance),
      });
    }
  }, [balance, estimate]);

  const gateStatus = estimate
    ? costGateStatus(estimate.total_estimated_tokens, balance)
    : 'affordable';
  const hasEnoughTokens = gateStatus !== 'insufficient';

  if (pendingHint) {
    return (
      <div className="text-right">
        <p className="text-sm text-white/50">{pendingHint}</p>
      </div>
    );
  }

  // Loading state - only show spinner when no estimate yet
  if (loading && !estimate) {
    return (
      <div className="text-right">
        <div className="text-xs text-white/40">{t('tokens.totalPipelineCost')}</div>
        <div className="flex items-center justify-end gap-2 mt-0.5">
          <Loader2 className="h-4 w-4 animate-spin text-white/40" />
          <span className="text-sm text-white/50">{t('tokens.calculating')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-right">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  // No estimate yet
  if (!estimate) return null;

  // Desktop (Electron) can run components on the user's own GPU, so it shows the
  // "Cloud X / Local Y" dual display + a local/partial/cloud badge. The web app
  // has no local hardware, so it shows a single cloud total.
  const showLocal = isElectronMode();
  const localTokens = estimate.total_estimated_tokens;
  // total_cloud_tokens is a newer response field. The desktop app's frontend can
  // run ahead of the deployed Cloud Run backend (it is bundled via --build-local
  // but the estimate is fetched from Cloud Run), so the field may be absent until
  // the backend is deployed. Fall back to the charged total so the card degrades
  // gracefully (cloud == local, no savings shown) instead of crashing the tab.
  const cloudTokens = estimate.total_cloud_tokens ?? localTokens;

  // Cost state: "local" when everything runs free on the user's GPU, "partial"
  // when some components are local and some are billed, "cloud" when nothing is
  // discounted locally.
  const costState: 'local' | 'partial' | 'cloud' =
    localTokens === 0 && cloudTokens > 0
      ? 'local'
      : cloudTokens > localTokens
        ? 'partial'
        : 'cloud';

  // Hide components that do not run for this configuration (e.g. phonetic
  // correction toggled off) — they are noise, not part of the actual cost.
  const visibleBreakdown = estimate.breakdown.filter((item) => item.location !== LOCATION_SKIPPED);

  // Right-hand cost cell for a single breakdown row, coloured by where it runs.
  const renderRowCost = (item: PipelineBreakdownComponent) => {
    if (item.location === LOCATION_LOCAL) {
      return (
        <span className="flex items-center gap-1 text-xs text-green-400">
          <Cpu className="h-3 w-3" />
          {t('tokens.free', { defaultValue: 'Free' })}
        </span>
      );
    }
    if (item.location === LOCATION_USER_KEY) {
      return (
        <span className="flex items-center gap-1 text-xs text-white/50">
          <KeyRound className="h-3 w-3" />
          {t('tokens.freeViaYourKey', { defaultValue: 'Free (your key)' })}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-xs text-white/60">
        <Cloud className="h-3 w-3 text-white/40" />
        {item.tokens === 0
          ? t('tokens.free', { defaultValue: 'Free' })
          : item.tokens.toLocaleString()}
      </span>
    );
  };

  // Local/partial/cloud chip shown next to the breakdown toggle on desktop.
  const renderStateBadge = () => {
    if (costState === 'local') {
      return (
        <span className="flex items-center gap-1 rounded-full border border-green-500/30 bg-green-500/15 px-2 py-0.5 text-[10px] font-medium text-green-300">
          <Cpu className="h-3 w-3" />
          {t('tokens.stateLocal', { defaultValue: 'Local' })}
        </span>
      );
    }
    if (costState === 'partial') {
      return (
        <span className="flex items-center gap-1 rounded-full border border-green-500/30 bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-300">
          <Cpu className="h-3 w-3" />
          <Cloud className="h-3 w-3 text-white/40" />
          {t('tokens.statePartial', { defaultValue: 'Partial' })}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-white/50">
        <Cloud className="h-3 w-3" />
        {t('tokens.stateCloud', { defaultValue: 'Cloud' })}
      </span>
    );
  };

  return (
    <div className="space-y-2">
      {/* Total cost row */}
      <div className="flex items-center justify-between gap-2">
        {/* Breakdown toggle + cost-state badge */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => setShowBreakdown((prev) => !prev)}
            className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors min-h-[44px] min-w-[44px] px-1"
            aria-expanded={showBreakdown}
            aria-label={showBreakdown ? t('tokens.hideBreakdown') : t('tokens.showBreakdown')}
          >
            {showBreakdown ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            <span>{t('tokens.pipelineBreakdown')}</span>
          </button>
          {showLocal && renderStateBadge()}
        </div>

        {/* Total: dual Cloud/Local on desktop, single cloud total on web — the
            SAME shared cell used by every single-operation tab (TokenEstimateCard). */}
        <CloudLocalCostDisplay
          cloudTokens={cloudTokens}
          localTokens={localTokens}
          showLocal={showLocal}
          hasEnough={hasEnoughTokens}
          loading={loading}
          cloudLabel={t('tokens.totalPipelineCost')}
        />
      </div>

      {/* Insufficient tokens warning */}
      {gateStatus === 'insufficient' && balance !== null && (
        <div className="flex items-center gap-1.5 text-red-400 text-xs justify-end">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span>
            {t('tokens.needMoreTokens', {
              count: estimate.total_estimated_tokens - balance,
            })}
          </span>
        </div>
      )}

      {/* Expandable breakdown — skipped components are hidden; each visible row
          is coloured by where it runs (green=local GPU, gray=cloud/BYOK). */}
      {showBreakdown && (
        <div className="rounded-lg bg-black/20 border border-white/5 p-3 space-y-1.5">
          {visibleBreakdown.map((item) => (
            <div key={item.component} className="flex items-center justify-between gap-2">
              <div className="flex flex-col min-w-0">
                <span
                  className={`text-xs truncate ${
                    item.location === LOCATION_LOCAL ? 'text-green-400/90' : 'text-white/70'
                  }`}
                >
                  {t(getComponentKey(item.component), {
                    defaultValue: item.component,
                  })}
                </span>
                <span className="text-[10px] text-white/30 truncate">{item.detail}</span>
              </div>
              <div className="text-right flex-shrink-0">{renderRowCost(item)}</div>
            </div>
          ))}

          {/* Divider + Total row */}
          <div className="border-t border-white/10 pt-1.5 flex items-center justify-between">
            <span className="text-xs text-white/50">{t('tokens.total')}</span>
            <span
              className={`text-xs font-semibold ${hasEnoughTokens ? 'text-green-400' : 'text-red-400'}`}
            >
              {estimate.total_estimated_tokens.toLocaleString()} tokens
            </span>
          </div>
        </div>
      )}
    </div>
  );
});
